import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchBusinessApi, fetchBusinessInvoiceApi } from '~/api';
import { formSlice, IFormData } from '~/store/slices/formSlice';
import { selectBusinessUuid, selectForm } from '~/store/slices/formSlice/selectors';
import { IInvoiceSerializer } from '~/types/invoice';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { serializeInvoiceData } from './helpers';
import { IBusiness } from '~/types/business';
import { useBoolean } from '~/hooks/useBoolean';

interface IUseInvoiceFormProps {
  isPhone?: boolean;
}

export function useInvoiceForm({ isPhone = false }: IUseInvoiceFormProps) {
  const dispatch = useDispatch();
  const formData = useSelector(selectForm);
  const businessUuid = useSelector(selectBusinessUuid);
  const [isLoading, loadingActions] = useBoolean();

  const [searchParams] = useSearchParams();
  const editInvoiceUuid = searchParams.get('editUuid') as string;

  function setValue(newValues: Partial<IFormData>) {
    dispatch(formSlice.actions.setValue(newValues));
  }

  async function getInvoiceData() {
    loadingActions.on();
    const promise = await fetchBusinessInvoiceApi(businessUuid, editInvoiceUuid);
    const response = await apiResponseHandler<IInvoiceSerializer>(promise);
    if (response.type === ApiResponseEnum.Success) {
      const serializedData = serializeInvoiceData(response.data);
      setValue(serializedData);
    } else {
      toast.error('Failed to fetch Invoice data');
    }
    loadingActions.off();
  }

  async function getBusinessData() {
    const promise = await fetchBusinessApi(businessUuid);
    const response = await apiResponseHandler<IBusiness>(promise);

    if (response.type === ApiResponseEnum.Success) {
      const businessData = response.data;
      const preFillData: Partial<IFormData> = {
        logoUrl: businessData.logo_url,
        terms: businessData.terms,
        notes: businessData.notes,
        discount: businessData.discount.toString(),
        discountType: businessData.discount_type,
        tax: businessData.tax.toString(),
        taxType: businessData.tax_type,
        shipping: businessData.shipping.toString(),
        shippingType: businessData.shipping_type,
        template_id: businessData.template_id,
        currency: businessData.currency,
      };
      setValue(preFillData);
    } else {
      toast.error('Failed to fetch Business data');
    }
  }

  useEffect(() => {
    // In case of edit, pre fill the form
    if (editInvoiceUuid) {
      getInvoiceData();
    } else {
      // Else fill the default values from business
      getBusinessData();
      // Empty the item list
      if (isPhone) {
        setValue({ items: [] });
      }
    }

    return () => {
      dispatch(formSlice.actions.resetForm());
    };
  }, []);

  return { formData, setValue, isLoading };
}
